import { defineMessages } from 'react-intl';

// messages are defined using snake case, because they have to match
// error codes sent from backend
export const warningTranslations = defineMessages({
  boolean_column_invalid: {
    defaultMessage: 'column is type: boolean. Valid values are true or false.',
    id: 'HbJo8S',
    description: 'Boolean value invalid error message',
  },
  enumeration_column_invalid: {
    defaultMessage: 'column is type: enumeration. Valid values are {constraintValue}.',
    id: 'UIfb5h',
    description: 'Enumeration value invalid error message',
  },
  string_column_truncated: {
    defaultMessage: 'column truncated because it exceeded the maximum number of characters: {constraintValue}.',
    id: '6tFtD+',
    description: 'String value truncated error message',
  },
  decimal_column_invalid: {
    defaultMessage: 'column requires a decimal value.',
    id: 'KA2Mk4',
    description: 'Decimal value invalid error message',
  },
  integer_column_invalid: {
    defaultMessage: 'column requires a numeric value.',
    id: 'qMsAxx',
    description: 'Integer value invalid error message',
  },
  number_column_over_max_value: {
    defaultMessage: 'column value exceeded the maximum number allowed: {constraintValue}.',
    id: 'djaJAD',
    description: 'Number value over max value error message',
  },
  number_column_under_min_value: {
    defaultMessage: 'column value is less than the minimum number allowed: {constraintValue}.',
    id: 'r2XGfF',
    description: 'Number value under min value error message',
  },
  inactive_product: {
    defaultMessage: 'Skipping record because this product is marked inactive.',
    id: 'whDat7',
    description: 'Inactive product error message',
  },
  matching_parent_product: {
    defaultMessage: 'Skipped record. This matched the parent record of a product with variants.',
    id: 'aGQN4y',
    description: 'Matching parent product error message',
  },
  alias_matching_restrictions: {
    defaultMessage: 'Skipped record. Matched a product alias.',
    id: 'k+ND61',
    description: 'Alias match restrictions error message',
  },
  missing_product_lot_id: {
    defaultMessage: 'Product Lot ID is missing. The field must be non empty when ExpirationDate is set.',
    id: 'lgKq+J',
    description: 'Missing product lot ID error message',
  },
  date_time_column_invalid: {
    defaultMessage: 'column is type: date. Suggested date format: mm/dd/yy.',
    id: 'lJ3EgJ',
    description: 'Date value invalid error message',
  },
  supplier_missing_seller_sku: {
    defaultMessage: 'Product SKU is missing. The field is required to associate the product with a supplier.',
    id: 'KIl2mO',
    description: 'Missing product SKU error message',
  },
  uom_type_each_skipped: {
    defaultMessage: "Rows with UOM type 'Each' are skipped — 'Each' is represented by the product itself.",
    id: 'JoDCEg',
    description: 'Each UOM type skipped warning message',
  },
  partial_deduction: {
    defaultMessage: 'Partial inventory deduction - not enough stock to fulfill the complete request.',
    id: '/ee/s7',
    description: 'Partial deduction warning message',
  },
  lookup_column_unrecognized_value: {
    defaultMessage: 'column has an unrecognized value: {constraintValue}.',
    id: 'REuuUZ',
    description: 'Lookup column unrecognized value warning message',
  },
});

export const errorTranslations = defineMessages({
  required_column_empty: {
    defaultMessage: 'column is required.',
    id: 'VlQGB6',
    description: 'Required column empty error message',
  },
  decimal_column_invalid: {
    defaultMessage: 'column is required. Column requires a decimal value.',
    id: '1v+7cD',
    description: 'Required decimal value invalid error message',
  },
  integer_column_invalid: {
    defaultMessage: 'column is required. Column requires a numeric value.',
    id: '4VkgeR',
    description: 'Required integer value invalid error message',
  },
  number_column_over_max_value: {
    defaultMessage: 'column is required, but column value exceeded the maximum number allowed: {constraintValue}.',
    id: '0j3Skw',
    description: 'Required number value over max value error message',
  },
  number_column_under_min_value: {
    defaultMessage: 'column is required, but column value is less than the minimum number allowed: {constraintValue}.',
    id: 'OoX15c',
    description: 'Required number value under min value error message',
  },
  db_error: {
    defaultMessage: 'Unable to save record. If this error persists, please contact support.',
    id: 'A7LFXc',
    description: 'Database import error message',
  },
  not_enough_stock: {
    defaultMessage: 'Not enough items in stock to continue saving this record.',
    id: '7Y/LjQ',
    description: 'Not enough stock error message',
  },
  missing_product_name: {
    defaultMessage: 'Product Name is missing. The field must be a valid text entry. Empty values are not allowed.',
    id: 'sbkDDZ',
    description: 'Missing product name error message',
  },
  date_time_column_invalid: {
    defaultMessage: 'column is required. Column requires a date value. Suggested date format: mm/dd/yy.',
    id: 'niCyB1',
    description: 'Required date value invalid error message',
  },
  invalid_parent_sku: {
    defaultMessage:
      'and SKU cannot be the same value. If this row represents a Parent product, leave the Parent SKU field blank. If this row represents a Variant product, specify a different SKU as the Parent SKU.',
    id: 'p6/wTA',
    description: 'invalid parent sku error message',
  },
  duplicate_package_name: {
    defaultMessage: 'A custom package named "{constraintValue}" already exists. Use a different name for this package.',
    id: 'FQbPuJ',
    description: 'Duplicate custom package name error message',
  },
  tote_name_already_exists: {
    defaultMessage: 'A tote named "{constraintValue}" already exists in this warehouse. Use a different name.',
    id: 'JYcPJM',
    description: 'Duplicate tote name error message',
  },
  tote_barcode_already_exists: {
    defaultMessage:
      'A tote with barcode "{constraintValue}" already exists. Barcodes must be unique across your warehouses. Use a different barcode.',
    id: '51U0Hu',
    description: 'Duplicate tote barcode error message',
  },
  uom_sku_belongs_to_another_product: {
    defaultMessage: 'UOM SKU "{constraintValue}" is already used by a unit of measure on a different product.',
    id: '+uGsUe',
    description: 'UOM SKU already assigned to a unit of measure on another product',
  },
  uom_barcode_belongs_to_another_product: {
    defaultMessage: 'Barcode "{constraintValue}" is already used by a unit of measure on a different product.',
    id: 'pPIj8p',
    description: 'UOM barcode already assigned to a unit of measure on another product',
  },
  uom_identifier_conflict: {
    defaultMessage:
      'This row has conflicting UOM identifiers — the UOM SKU and barcode match different existing records, or the same barcode appears on multiple rows in this file.',
    id: 'ZmP4Kl',
    description: 'UOM SKU and barcode identify different existing records, or duplicate barcode within the file',
  },
  uom_ambiguous_match: {
    defaultMessage: 'Multiple existing UOM records match this row — provide a UOM SKU or barcode to identify which record to update.',
    id: 'AvKAtL',
    description: 'Multiple existing UOM records match the row with no unique identifier to disambiguate',
  },
  uom_deleted_during_import: {
    defaultMessage: 'Unable to update unit of measure: it was deleted after this import started.',
    id: 'lZ6Di6',
    description: 'Unit of measure was deactivated between the import snapshot and this row being written',
  },
  duplicate_sku: {
    defaultMessage: 'SKU {constraintValue} is already in use by another product, product alias, or unit of measure.',
    id: 'F9r11+',
    description: 'Duplicate SKU import error message',
  },
});
